import type { RecordMerchant } from 'features/table-columns/cells'
import { DiscountParams, constructorDiscounts } from 'models/Discounts'
import { NfseScheduled } from 'models/NFe'
import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { RecurrencesParams } from 'models/Recurrences'

export interface Products {
  // SC-10999: só nas respostas agregadas (visão multi-empresas)
  merchant?: RecordMerchant | null
  chargeType: string
  dueType: string
  proratedCharge: boolean
  amount: string
  createdAt: string
  description: string
  discountPolicy: string
  dueDay: number
  frequency: string
  frequency_type?: 'days' | 'weeks' | 'months' | 'years'
  frequency_number?: number
  icon: string
  metadata: Record<string, string> | undefined
  bonus:
    | {
        uuid: string
        amount: string
        policy: string
        total_cycles: number
        start_cycle?: number
        bonusable_type: string
        created_at: string
        updated_at: string
      }
    | undefined
  interestFee: string
  interestFine: string
  interestPolicy: string
  nfseScheduled?: NfseScheduled
  nfsePolicy?: string
  nfseIssuerUuid?: string
  merchantUuid: string
  name: string
  paymentType: string[]
  status: string
  totalCycles: number
  updatedAt: string
  uuid: string
  charges?: ProductCharge[]
  discounts?: DiscountParams[]
  billingDescription?: string
  documentFile?: DocumentFile
  productInstallment?: ProductInstallment
  hasBonus?: boolean
  bonusSettings?: {
    cyclesCount: number
    type: 'value' | 'percentage'
    amount: string
  }
  negativation?: {
    days_to_report: number
    negativation?: {
      uuid: string
      status: string
      status_detail: any
      created_at: string
      updated_at: string
      customer_uuid: string
      merchant_uuid: string
      billing_uuid: string
    }
  }
  tags?: any[]
  // Dados de negativação

  // Campo de expiração para produtos
  limit_days_for_payment?: number
  // Nova estrutura de meios de pagamento
  paymentTypeDetails?: PaymentTypeDetails
  issue_days?: number
  issue_billing_type?: 'days_before_due_date' | 'specific_day'
  adjustment_scheduled?: AdjustmentScheduledParams
  historic?: Array<{
    event: string
    description: string
    date_change: string
    user: string
  }>
}

export interface AdjustmentScheduledParams {
  uuid?: string
  adjustment_type: 'customized' | 'ipca' | 'igpm'
  frequency: 'quarterly' | 'semester' | 'annual'
  percentage_value?: string | number | null
  first_adjustment_date?: string
  next_adjustment_date?: string
  automatic_application?: boolean
}

type DocumentFile = {
  document_fileable_type: string
  uuid: string
  doc_type: string
  status: string
  description: string
  created_at: string
  updated_at: string
  thumbnail_url: string
}

export interface ProductInstallment {
  uuid: string
  maxInstallments: number
  maxInstallmentsWithoutFee: number
  createdAt: string
  updatedAt: string
}

export interface ProductCharge {
  uuid: string
  customer: {
    uuid: string
    name: string
    document: string
  }
  description: string
  totalCycles: number
  currentCycle: number
  type: 'Billing' | 'Recurrence'
  tags?: string
  amountBilled?: string
  status?: string
  /** Só vem preenchida em Billing; o encoder manda null para Recurrence */
  dueDate?: string
}

function constructorProductCharge(data: any): ProductCharge {
  return {
    uuid: data?.uuid,
    customer: {
      uuid: data?.customer?.uuid,
      name: data?.customer?.name,
      document: data?.customer?.document
    },
    description: data?.description,
    totalCycles: data?.total_cycles,
    currentCycle: data?.current_cycle,
    type: data?.type,
    tags: data?.tags,
    amountBilled: data?.amount_billed,
    status: data?.status,
    dueDate: data?.due_date
  }
}

function contructorProductInstallment(data: any): ProductInstallment {
  return {
    uuid: data?.uuid,
    maxInstallments: data?.max_installments,
    maxInstallmentsWithoutFee: data?.max_installments_without_fee,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  }
}

export function constructorProducts(data: any): Array<Products> {
  return data?.map((product: any) => {
    const mappedProduct = {
      merchant: product?.merchant ?? null,
      amount: product?.amount,
      createdAt: product?.created_at,
      description: product?.description,
      billingDescription: product?.billing_description,
      discountPolicy: product?.discount_policy,
      dueDay: product?.due_day,
      frequency: product?.frequency,
      frequency_type: product?.frequency_type,
      frequency_number: product?.frequency_number,
      icon: product?.icon,
      interestFee: product?.interest_fee,
      interestFine: product?.interest_fine,
      interestPolicy: product?.interest_policy,
      nfseIssuerUuid: product?.nfse_issuer_uuid,
      nfsePolicy: product?.nfse_policy,
      nfseScheduled: product?.nfse_scheduled,
      merchantUuid: product?.merchant_uuid,
      name: product?.name,
      bonus: product.bonus,
      paymentType: product?.payment_type
        ? product?.payment_type.split(';')
        : [],
      tags: Array.isArray(product?.tags)
        ? product.tags
        : typeof product?.tags === 'string' && product.tags.length > 0
        ? product.tags
            .split(';')
            .map((tagId: string) => ({ id: tagId.trim(), name: tagId.trim() }))
        : [],
      status: product?.status,
      totalCycles: product?.total_cycles,
      updatedAt: product?.updated_at,
      uuid: product?.uuid,
      charges: product?.charges
        ? product?.charges.map((charge: RecurrencesParams) =>
            constructorProductCharge(charge)
          )
        : [],
      discounts: product?.discounts
        ? constructorDiscounts(product.discounts)
        : undefined,
      chargeType: product?.charge_type,
      dueType: product?.due_type,
      proratedCharge: product?.prorated_charge,
      documentFile: product?.document_file,
      productInstallment: contructorProductInstallment(
        product?.product_installment
      ),
      // Processar dados de bonificação
      hasBonus: product?.bonus_policy !== 'no_bonus',
      bonusSettings:
        product?.bonus_policy !== 'no_bonus' && product?.bonus
          ? {
              cyclesCount: product.bonus.total_cycles,
              type: product?.bonus_policy === 'fixed' ? 'value' : 'percentage',
              amount: product.bonus.amount
            }
          : undefined,
      revenueCategory: product?.revenue_category
        ? {
            uuid: product.revenue_category.uuid,
            name: product.revenue_category.name,
            color: product.revenue_category.color
          }
        : undefined,
      // Processar dados de negativação
      negativation: product?.negativation,
      // Incluir metadados para identificadores internos
      metadata: product?.metadata,
      // Incluir campo de expiração
      limit_days_for_payment: product?.limit_days_for_payment,
      // Nova estrutura de meios de pagamento
      paymentTypeDetails: product?.payment_type_details,
      issue_days: product?.issue_days,
      issue_billing_type: product?.issue_billing_type,
      historic: product?.historic,
      adjustment_scheduled: product?.adjustment_scheduled
    } as Products

    return mappedProduct
  })
}
